import _ from 'lodash'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Formik, FormikProps } from 'formik'
import * as yup from 'yup'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import LoadingButton from '@mui/lab/LoadingButton'

import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { periodGenerator } from '../AvailabilityForm'

import MeetingPlaceStep from './steps/MeetingPlaceStep'
import SpecialitiesStep from './steps/SpecialitiesStep'
import ScheduleStep from './steps/ScheduleStep'
import LocationScheduleStep from './steps/LocationScheduleStep'
import ServiceAreaScheduleStep from './steps/ServiceAreaScheduleStep'
import SecondOpinionReviewStep from './steps/SecondOpinionReviewStep'
import ReviewStep from './steps/ReviewStep'

dayjs.extend(duration)

export type AvailabilityWizardValues = {
  meetingPlace: AvailabilityMeetingPlace
  specialities: string[]
  avgMeetingDuration: number
  address: { description: string; placeId: string } | null
  additionalAddress: string
  serviceArea: {
    allowedPostalCodes: string
    center: { description?: string; placeId?: string; lat?: number; lng?: number } | null
    radiusKm: number | ''
  }
  recurrenceDays: Array<{ day: number; periods: Array<{ open: string; close: string }> }>
  formTemplate: string
  notes: string
}

type CreateAvailabilityWizardProps = {
  isSubmitting?: boolean
  onSubmit: (values: AvailabilityWizardValues) => void
  specialities: string[]
}

const DEFAULT_RECURRENCE_DAYS = (): AvailabilityWizardValues['recurrenceDays'] =>
  _.range(7).map((day) => ({
    day,
    periods: day >= 1 && day <= 5
      ? [periodGenerator('09:00', '12:00'), periodGenerator('14:00', '18:00')]
      : [],
  }))

export default function CreateAvailabilityWizard({ isSubmitting, onSubmit, specialities }: CreateAvailabilityWizardProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [activeStep, setActiveStep] = useState(0)
  const formikRef = useRef<FormikProps<AvailabilityWizardValues>>(null)

  const initialValues: AvailabilityWizardValues = useMemo(
    () => ({
      meetingPlace: AvailabilityMeetingPlace.CENTER,
      specialities: [],
      avgMeetingDuration: 30,
      address: null,
      additionalAddress: '',
      serviceArea: {
        allowedPostalCodes: '',
        center: null,
        radiusKm: '',
      },
      recurrenceDays: DEFAULT_RECURRENCE_DAYS(),
      formTemplate: '',
      notes: '',
    }),
    []
  )

  const getStepLabels = (meetingPlace: AvailabilityMeetingPlace): string[] => {
    const base = [
      t('page.meetingLink.wizard.step.meetingPlace', 'Meeting Place'),
      t('page.meetingLink.wizard.step.specialities', 'Specialities'),
    ]

    if (meetingPlace === AvailabilityMeetingPlace.SECOND_OPINION) {
      return [...base, t('page.meetingLink.wizard.step.review', 'Review')]
    }

    const step3Labels: Record<string, string> = {
      [AvailabilityMeetingPlace.VISIO_CONFERENCE]: t('page.meetingLink.wizard.step.schedule', 'Schedule'),
      [AvailabilityMeetingPlace.CENTER]: t('page.meetingLink.wizard.step.locationSchedule', 'Location & Schedule'),
      [AvailabilityMeetingPlace.AT_HOME]: t('page.meetingLink.wizard.step.serviceArea', 'Service Area & Schedule'),
    }

    return [
      ...base,
      step3Labels[meetingPlace] || t('page.meetingLink.wizard.step.schedule', 'Schedule'),
      t('page.meetingLink.wizard.step.review', 'Review'),
    ]
  }

  const validationSchemas = useMemo(() => {
    const step1 = yup.object().shape({ meetingPlace: yup.string().required() })
    const step2 = yup.object().shape({
      specialities: yup.array().of(yup.string()).min(1, t('error.specialities.min.message', 'Select at least one speciality')),
      avgMeetingDuration: yup.number().transform((val, orig) => (orig === '' || orig === null ? undefined : val)).required(t('error.avgMeetingDuration.required.message', 'Duration is required')).min(1, t('error.avgMeetingDuration.min.message', 'Duration must be at least 1 minute')),
    })
    const scheduleSchema = yup.object({
      recurrenceDays: yup.array().of(
        yup.object().shape({
          periods: yup.array().of(
            yup.object({
              open: yup.string().required().matches(/^\d\d:\d\d$/, t('error.time.invalidFormat.message')),
              close: yup.string().required().matches(/^\d\d:\d\d$/, t('error.time.invalidFormat.message'))
                .test('is-greater', t('error.time.endTimeSoonerThanStartTime.message'), (value, ctx) => {
                  const { open } = ctx.parent
                  return dayjs(value, 'HH:mm').isSameOrAfter(dayjs(open, 'HH:mm'))
                }),
            })
          ),
        })
      ).test('at-least-one-day', t('page.meetingLink.wizard.schedule.atLeastOneDay', 'Select at least one day'), (days) => {
        return days ? days.some((d) => d.periods && d.periods.length > 0) : false
      }),
    })
    const locationSchema = yup.object({
      address: yup.object({ description: yup.string(), placeId: yup.string() }).nullable()
        .required(t('error.address.required.message')),
    }).concat(scheduleSchema)
    const serviceAreaSchema = scheduleSchema
    const reviewSchema = yup.object({})

    return { step1, step2, scheduleSchema, locationSchema, serviceAreaSchema, reviewSchema }
  }, [t])

  const getValidationSchema = (step: number, meetingPlace: AvailabilityMeetingPlace) => {
    if (step === 0) return validationSchemas.step1
    if (step === 1) return validationSchemas.step2
    if (step === 2) {
      if (meetingPlace === AvailabilityMeetingPlace.SECOND_OPINION) return validationSchemas.reviewSchema
      if (meetingPlace === AvailabilityMeetingPlace.CENTER) return validationSchemas.locationSchema
      if (meetingPlace === AvailabilityMeetingPlace.AT_HOME) return validationSchemas.serviceAreaSchema
      return validationSchemas.scheduleSchema
    }
    return validationSchemas.reviewSchema
  }

  const isSecondOpinion = formikRef.current?.values?.meetingPlace === AvailabilityMeetingPlace.SECOND_OPINION
  const totalSteps = isSecondOpinion ? 3 : 4
  const isLastStep = activeStep === totalSteps - 1

  const handleNext = async () => {
    if (!formikRef.current) return
    const { values } = formikRef.current
    const schema = getValidationSchema(activeStep, values.meetingPlace)

    try {
      await schema.validate(values, { abortEarly: false, stripUnknown: true })
      if (isLastStep) {
        formikRef.current.submitForm()
      } else {
        setActiveStep((prev) => prev + 1)
      }
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const errors: Record<string, string> = {}
        const touchedFields: Record<string, boolean> = {}
        err.inner.forEach((e) => {
          if (e.path) {
            _.set(touchedFields, e.path, true)
            _.set(errors, e.path, e.message)
          }
        })
        formikRef.current.setTouched({ ...formikRef.current.touched, ...touchedFields })
        formikRef.current.setErrors(errors)
      }
    }
  }

  const handleBack = () => setActiveStep((prev) => prev - 1)

  const renderStep = (step: number, meetingPlace: AvailabilityMeetingPlace) => {
    if (step === 0) return <MeetingPlaceStep />
    if (step === 1) return <SpecialitiesStep specialities={specialities} />
    if (step === 2) {
      if (meetingPlace === AvailabilityMeetingPlace.SECOND_OPINION) return <SecondOpinionReviewStep />
      if (meetingPlace === AvailabilityMeetingPlace.CENTER) return <LocationScheduleStep />
      if (meetingPlace === AvailabilityMeetingPlace.AT_HOME) return <ServiceAreaScheduleStep />
      return <ScheduleStep />
    }
    return <ReviewStep />
  }

  return (
    <Formik
      initialValues={initialValues}
      innerRef={formikRef}
      onSubmit={onSubmit}
      validateOnChange={false}
      validateOnBlur
    >
      {({ values }) => {
        const stepLabels = getStepLabels(values.meetingPlace)

        return (
          <Form>
            <Stack spacing={3} sx={{ py: 2 }}>
              <Stepper
                activeStep={activeStep}
                alternativeLabel={!isMobile}
                orientation={isMobile ? 'vertical' : 'horizontal'}
              >
                {stepLabels.map((label, index) => (
                  <Step key={label} completed={index < activeStep}>
                    <StepLabel aria-current={index === activeStep ? 'step' : undefined}>
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ minHeight: 300, px: 1 }}>
                {renderStep(activeStep, values.meetingPlace)}
              </Box>

              <Stack direction="row" justifyContent="space-between" sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                  type="button"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                  data-testid="availability-wizard-back"
                >
                  {t('common:button.back.label', 'Back')}
                </Button>

                <LoadingButton
                  type="button"
                  loading={isSubmitting}
                  onClick={handleNext}
                  variant="contained"
                  data-testid={isLastStep ? 'availability-wizard-submit' : 'availability-wizard-next'}
                >
                  {isLastStep
                    ? t('page.meetingLink.wizard.button.create', 'Create Availability')
                    : t('common:button.next.label', 'Next')}
                </LoadingButton>
              </Stack>
            </Stack>
          </Form>
        )
      }}
    </Formik>
  )
}
